import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { itemsForShop } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { currentHouseholdId, listShopping } from '../db/shopping.js';
import { listShops, listTrips } from '../db/shops.js';
import { useToday } from '../hooks/useToday.js';

/**
 * Einkauf heute (18.10.2026): Am Einkaufstag steht auf „Heute“, wohin es geht
 * und wie viel auf der Liste dieses Ladens offen ist — ein Tipp öffnet genau
 * diese Liste.
 */
export function TodayShopping({
  onOpenList,
}: {
  onOpenList: (shopId: string | null) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const today = useToday();
  const data = useLiveQuery(async () => {
    if (!today) return null;
    const householdId = await currentHouseholdId(db);
    const [trips, shops, items] = await Promise.all([
      listTrips(householdId, today, db),
      listShops(householdId, db),
      listShopping(householdId, db),
    ]);
    return { trips: trips.filter((trip) => trip.date === today), shops, items };
  }, [today]);
  if (!data || data.trips.length === 0) return null;

  const live = new Set(data.shops.map((shop) => shop.id));
  const open = data.items.filter((item) => !item.checkedAt);
  return (
    <>
      {data.trips.map((trip) => {
        const shopId = trip.shopId && live.has(trip.shopId) ? trip.shopId : null;
        const shop = data.shops.find((entry) => entry.id === shopId)?.name ?? null;
        const count = itemsForShop(open, shopId, live).length;
        return (
          <section
            key={trip.id}
            aria-labelledby={`trip-${trip.id}`}
            className="kw-tile flex flex-col gap-2"
          >
            <h2 id={`trip-${trip.id}`} className="kw-kicker">
              {t('shops.todayHeading')}
            </h2>
            <p className="text-base font-semibold">
              {[trip.time, shop ?? t('shops.anywhere')].filter(Boolean).join(' · ')}
            </p>
            <p className="text-sm text-ink-muted">{t('shops.todayOpen', { count })}</p>
            <button
              type="button"
              onClick={() => onOpenList(shopId)}
              className="kw-btn kw-btn-small"
            >
              {t('shops.toList')}
            </button>
          </section>
        );
      })}
    </>
  );
}
